import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function toTitleCase(str: string) {
  return str.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

export function fixHangingZeroes(value: number, precision: number = 2) {
  return parseFloat(value.toFixed(precision));
}

export function roundToNearest(value: number, nearest: number) {
  return fixHangingZeroes(Math.round(value / nearest) * nearest);
}

export function roundAmount(amount: number) {
  if (amount < 0.5) return roundToNearest(amount, 0.01);
  if (amount < 10) return roundToNearest(amount, 0.1);
  return roundToNearest(amount, 1);
}

export function defined<T>(value: T | undefined | null): value is T {
  return value !== undefined && value !== null;
}

export function toNumber(
  value: string | number | null,
  defaultValue: number = 0,
): number {
  if (value === null) return defaultValue;

  const parsed = Number.parseFloat(String(value));
  return Number.isNaN(parsed) ? defaultValue : parsed;
}

export function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(value, max));
}

export function slugify(name: string): string {
  return name
    .normalize("NFKD") // Converts accents/ligatures/etc to latin alphabet
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Remove all invalid characters
    .trim()
    .replace(/[-\s]+/g, "-"); // Replace multiple spaces or hyphens with a single hyphen
}

export function partition<T>(
  array: T[],
  predicate: (item: T) => boolean,
): [T[], T[]] {
  return array.reduce<[T[], T[]]>(
    ([trueItems, falseItems], item) => {
      if (predicate(item)) {
        trueItems.push(item);
      } else {
        falseItems.push(item);
      }
      return [trueItems, falseItems];
    },
    [[], []],
  );
}
